import { supportLabel } from "../contexts/TenantContext";
/**
 * 엑셀 처리 실패 안내 유틸
 *
 * 실패 원인은 크게 둘이고, 사용자가 할 일이 서로 다르다.
 *
 *  1) timeout     — 파일 처리가 제한 시간 안에 안 끝났다. 시트를 나누거나 데이터를 줄이면 된다.
 *  2) unavailable — 엑셀 처리 서버에 닿지 못했다. 파일과 무관하므로 잠시 뒤 다시 하면 된다.
 *
 * 예전에는 이 둘을 "파일 데이터가 너무 많아..." 한 문구로 합쳐 안내했다.
 * 2026-08-12 엑셀 서버가 무응답이던 동안 240KB짜리 가벼운 파일을 올린 사용자에게도
 * "파일 데이터를 정리하라"고 안내가 나가, 고객이 멀쩡한 파일을 붙들고 헤매다 문의를 넣었다.
 * 원인이 다르면 안내도 달라야 한다.
 */

export type ExcelFailureKind = "timeout" | "unavailable" | "other";

// 원인을 파일로 단정하지 않는다.
// 이 문구는 파일을 막 올린 시점(시트 목록 조회)에서도 나가는데, 그때 사용자는 아직
// 시트를 고르지도 못한 상태다. "시트를 나눠서 실행하라"는 할 수 없는 일을 시키는 것이다.
// 실제로 느렸던 원인이 서버 쪽이었던 적도 있다(2026-09-01: 이름 제거가 O(n^2)라 60초를
// 넘겼고, 알고리즘만 고치니 같은 파일이 5초에 끝났다 — 고객 파일은 그대로였다).
const TIMEOUT_MESSAGE =
  "처리 시간이 초과되어 중단되었습니다.\n" +
  "잠시 후 다시 시도해주시고, 계속되면 고객센터로 문의해주세요.";

// 발주처 포털 사용자에겐 고객센터가 없다 → 화면에 내보내기 직전 "발주처 담당자" 로 바꾼다.
const withSupport = (msg: string): string => msg.replace(/고객센터/g, supportLabel());

const UNAVAILABLE_MESSAGE =
  "엑셀 처리 서버가 일시적으로 응답하지 않습니다.\n" +
  "파일 문제가 아니니 잠시 후 다시 시도해주세요.\n" +
  "계속되면 고객센터로 문의해주세요.";

// 처리 시간 초과 쪽 상태 코드. 408 Request Timeout / 504 Gateway Timeout.
const TIMEOUT_STATUS_CODES = new Set([408, 504]);
// 서버에 닿지 못한 쪽. 502 Bad Gateway / 503 Service Unavailable.
const UNAVAILABLE_STATUS_CODES = new Set([502, 503]);

/**
 * 실패 원인을 판별한다. 백엔드가 보내주는 reason 을 가장 신뢰하고,
 * 없으면 상태 코드 → 본문 문구 → axios 수준 신호 순으로 본다.
 */
export const classifyExcelFailure = (error: any): ExcelFailureKind => {
  if (!error) return "other";

  if (error.response) {
    // 백엔드(dk-cost-api)가 원인을 직접 알려주는 경우 그대로 따른다.
    const reason: string | undefined = error.response.data?.reason;
    if (reason === "timeout" || reason === "unavailable") {
      return reason;
    }

    const status: number | undefined = error.response.status;
    if (typeof status === "number") {
      if (TIMEOUT_STATUS_CODES.has(status)) return "timeout";
      if (UNAVAILABLE_STATUS_CODES.has(status)) return "unavailable";
    }

    const bodyMessage: string | undefined =
      error.response.data?.message || error.response.data?.errorMessage;
    if (typeof bodyMessage === "string") {
      if (/(timeout|시간\s*초과|too large|too many)/i.test(bodyMessage)) {
        return "timeout";
      }
      if (/(응답하지\s*않|연결할\s*수\s*없)/.test(bodyMessage)) {
        return "unavailable";
      }
    }

    return "other";
  }

  // 여기서부터는 응답 자체가 없는 경우다.

  // axios 가 스스로 기다리다 끊은 것 → 처리 시간 초과
  const code: string | undefined = error.code;
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") return "timeout";

  const msg: string | undefined = error.message;
  if (typeof msg === "string" && /timeout|timed out|시간\s*초과/i.test(msg)) {
    return "timeout";
  }

  // 요청은 나갔는데 응답이 없다 → 서버가 죽었거나 네트워크가 끊겼다.
  // 여기를 timeout 으로 뭉뚱그리면 서버 장애 때 파일 탓을 하게 된다.
  if (error.request) return "unavailable";

  return "other";
};

/**
 * 엑셀 처리 실패로 볼 수 있는 상황인지 (timeout / unavailable 둘 중 하나).
 */
export const isExcelOverloadError = (error: any): boolean =>
  classifyExcelFailure(error) !== "other";

/**
 * 엑셀 처리 catch 블록에서 사용. 원인에 맞는 안내를 돌려주고,
 * 판별이 안 되면 호출측이 준 fallback 을 쓴다.
 */
export const getExcelErrorMessage = (error: any, fallback: string): string => {
  switch (classifyExcelFailure(error)) {
    case "timeout":
      return withSupport(TIMEOUT_MESSAGE);
    case "unavailable":
      return withSupport(UNAVAILABLE_MESSAGE);
    default:
      return withSupport(fallback);
  }
};

export const EXCEL_TIMEOUT_MESSAGE = TIMEOUT_MESSAGE;
export const EXCEL_UNAVAILABLE_MESSAGE = UNAVAILABLE_MESSAGE;
